import {
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { RowSelectionState } from "@tanstack/react-table";
import { BULK_CONFIRM_COUNTERPARTIES_RESOLVED_ERRORS } from "@common/helpers/financialDocument";
import { pluralize, pluralizeWithCount } from "@common/helpers/string";
import { FiltersTypes } from "@common/types/filters/general";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { ViewToolbar } from "@components/views/ViewToolbar/ViewToolbar";
import { AccrualImporterDraftsCommandBar } from "./AccrualImporterDraftsCommandBar";
import { useAccrualDraftBulkCommandActions } from "./useAccrualDraftBulkCommandActions";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { useConfirmAccrualCounterpartiesMutation } from "@api/financialDocument.api";
import { useEntities } from "@hooks/useEntities";
import { useSubNavigationPortalContent } from "@hooks/useSubNavigationPortal";
import { AccrualImporterContext } from "./accrualImporter.context";
import {
    confirmableDocumentsForSelection,
    countAccrualRowsWithImporterIssues,
    hasUnconfirmedAccrualCounterparty,
} from "./accrualDraftBulkEligibility";
import { accrualDocumentBeingProcessed } from "./accrualImporter.helpers";
import {
    buildAccrualDraftGroupedRows,
    discardedDraftsMessage,
    discardQuestionForSelection,
    documentRowsForDraftSelection,
    financialDocumentsForDraftSelection,
    getAccrualDraftLineItemSiblings,
    omittedExtractionCountForDiscard,
} from "./accrualImporterDraftGroups";
import { AccrualImporterTableMeta } from "./accrualDocumentImporterCells";
import {
    AccrualImporterDraftSort,
    sortAccrualImporterDraftRows,
} from "./accrualImporterDraftsSort";
import { AccrualImporterQuickLook } from "./AccrualImporterQuickLook";
import { filterAccrualImporterDraftRows } from "./accrualImporterDraftFilters";
import { AccrualImporterStats } from "./AccrualImporterStats";
import {
    AccrualImporterDocumentRow,
    AccrualImporterViewRow,
} from "./accrualImporter.types";
import { useAccrualImporterPageView } from "./useAccrualImporterPageView";
import { useRemoveAccrualImporterDocument } from "./useRemoveAccrualImporterDocument";
import { useAccrualExtractionRerun } from "./useAccrualExtractionRerun";
import { useAccrualDocumentMove } from "./useAccrualDocumentMove";
import invoicesViewStyles from "@components/invoices/views/InvoicesView.module.scss";
import draftsViewStyles from "./AccrualImporterDraftsView.module.scss";

interface Props {
    search?: string;
    filters: FiltersTypes;
    pageView: ReturnType<typeof useAccrualImporterPageView>;
    onSelectedRowsChange: (rows: AccrualImporterViewRow[]) => void;
    onVisibleRowsChange: (rows: AccrualImporterDocumentRow[]) => void;
    selectionResetKey?: number;
    reviewAction?: ReactNode;
    onConfirmDocument: (financialDocumentId: number) => void;
    onConfirmDocuments: (financialDocumentIds: number[]) => void;
    confirmingDocumentIds?: readonly number[];
}

export const AccrualImporterDraftsTable: React.FC<Props> = ({
    search,
    filters,
    pageView,
    onSelectedRowsChange,
    onVisibleRowsChange,
    selectionResetKey = 0,
    reviewAction,
    onConfirmDocument,
    onConfirmDocuments,
    confirmingDocumentIds,
}) => {
    const { mode, documentRows, setPreviewedRowId } = useContext(
        AccrualImporterContext,
    );
    const [sort, setSort] = useState<AccrualImporterDraftSort | undefined>();
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
    const [focusedRow, setFocusedRow] = useState<AccrualImporterViewRow | null>(
        null,
    );
    const [isQuickLookOpen, setIsQuickLookOpen] = useState(false);
    const [rowsToDiscard, setRowsToDiscard] = useState<
        AccrualImporterViewRow[] | null
    >(null);
    const { removeDocuments } = useRemoveAccrualImporterDocument();
    const { requestRerun, rerunConfirm, isRerunning } =
        useAccrualExtractionRerun();
    const { moveDocuments, moveLabel, isMoving } = useAccrualDocumentMove();
    const { success, error } = useToaster();
    const entities = useEntities();
    const entityNames = useMemo(() => {
        const names = new Map<number, string>();
        for (const entity of entities) {
            if (entity.name) {
                names.set(entity.id, entity.name);
            }
        }
        return names;
    }, [entities]);
    const {
        tableView,
        onViewChange,
        groupBy,
        viewConfig,
        builtInViews,
        baseViewCacheKey,
        defaultFilters,
    } = pageView;

    const viewToolbar = useMemo(
        () => (
            <ViewToolbar
                view={tableView}
                onViewChange={onViewChange}
                viewConfig={viewConfig}
                builtInViews={builtInViews}
                defaultFilters={defaultFilters}
                baseViewCacheKey={baseViewCacheKey}
            />
        ),
        [
            baseViewCacheKey,
            builtInViews,
            defaultFilters,
            onViewChange,
            tableView,
            viewConfig,
        ],
    );
    useSubNavigationPortalContent(
        "accrual-importer-drafts-toolbar",
        viewToolbar,
    );

    const visibleRows = useMemo(
        () =>
            sortAccrualImporterDraftRows({
                rows: filterAccrualImporterDraftRows({
                    rows: documentRows,
                    filters,
                    search,
                    mode,
                }),
                sort,
                mode,
                entityNames,
            }),
        [documentRows, entityNames, filters, mode, search, sort],
    );
    useEffect(() => {
        onVisibleRowsChange(visibleRows);
    }, [onVisibleRowsChange, visibleRows]);

    const isInvoices = mode === "invoices";
    const pageData = useMemo(
        () =>
            buildAccrualDraftGroupedRows({
                rows: visibleRows,
                mode,
                groupBy,
                counterpartyNames: new Map(),
            }),
        [groupBy, mode, visibleRows],
    );
    useEffect(() => {
        if (selectionResetKey === 0) {
            return;
        }
        setRowSelection({});
    }, [selectionResetKey]);
    const checkSiblings = useCallback(
        (row: AccrualImporterViewRow) =>
            getAccrualDraftLineItemSiblings({ rows: pageData, row }),
        [pageData],
    );
    const tableMeta: AccrualImporterTableMeta = useMemo(
        () => ({
            callbacks: {
                checkSiblings,
                confirmDocument: onConfirmDocument,
                confirmingDocumentIds,
            },
        }),
        [checkSiblings, confirmingDocumentIds, onConfirmDocument],
    );
    const selectableRows = useMemo(
        () => pageData.filter((row) => row.financialDocument),
        [pageData],
    );
    const selectedRows = useMemo(
        () => selectableRows.filter((row) => rowSelection[row.id]),
        [rowSelection, selectableRows],
    );
    useEffect(() => {
        onSelectedRowsChange(selectedRows);
    }, [onSelectedRowsChange, selectedRows]);
    const selectAll = useCallback(() => {
        setRowSelection(
            Object.fromEntries(selectableRows.map((row) => [row.id, true])),
        );
    }, [selectableRows]);
    const discardSelection = useCallback(() => {
        setRowsToDiscard(selectedRows);
    }, [selectedRows]);
    const idleSelectedDocuments = useMemo(
        () =>
            financialDocumentsForDraftSelection(selectedRows).filter(
                (document) => !accrualDocumentBeingProcessed(document),
            ),
        [selectedRows],
    );
    const rerunSelection = useCallback(() => {
        requestRerun(idleSelectedDocuments);
    }, [idleSelectedDocuments, requestRerun]);
    const moveSelection = useCallback(() => {
        moveDocuments(idleSelectedDocuments);
    }, [idleSelectedDocuments, moveDocuments]);
    const counterpartyLabel = mode === "invoices" ? "customers" : "vendors";
    const { mutate: confirmCounterparties } =
        useConfirmAccrualCounterpartiesMutation();
    const confirmCounterpartiesSelection = useCallback(() => {
        const financialDocumentIds = financialDocumentsForDraftSelection(
            selectedRows,
        ).map((document) => document.id);
        confirmCounterparties(financialDocumentIds, {
            onSuccess: (confirmedCount) =>
                success(
                    confirmedCount === 0
                        ? `No ${counterpartyLabel} to confirm.`
                        : `Confirmed ${counterpartyLabel} on ${confirmedCount} ${pluralize("draft", confirmedCount)}.`,
                ),
            onError: () => error(`Failed to confirm ${counterpartyLabel}.`),
        });
    }, [
        confirmCounterparties,
        counterpartyLabel,
        error,
        selectedRows,
        success,
    ]);
    const canRerunSelection = !isRerunning && idleSelectedDocuments.length > 0;
    const canMoveSelection = !isMoving && idleSelectedDocuments.length > 0;
    const canConfirmCounterparties =
        selectedRows.some(
            (row) =>
                row.financialDocument != null &&
                hasUnconfirmedAccrualCounterparty(row.financialDocument),
        ) &&
        countAccrualRowsWithImporterIssues({
            rows: selectedRows,
            ignoredErrors: BULK_CONFIRM_COUNTERPARTIES_RESOLVED_ERRORS,
        }) === 0;
    const itemLabel = isInvoices ? "invoice" : "bill";
    const documentsToConfirm = useMemo(
        () => confirmableDocumentsForSelection(selectedRows),
        [selectedRows],
    );
    const confirmSelection = useCallback(() => {
        onConfirmDocuments(documentsToConfirm.map((document) => document.id));
    }, [documentsToConfirm, onConfirmDocuments]);
    const { handleOpenBulkActions } = useAccrualDraftBulkCommandActions({
        selectedCount: selectedRows.length,
        onConfirm: documentsToConfirm.length > 0 ? confirmSelection : undefined,
        confirmLabel: `Confirm ${pluralizeWithCount(itemLabel, documentsToConfirm.length)}`,
        onDiscard: discardSelection,
        onRerun: canRerunSelection ? rerunSelection : undefined,
        onMove: canMoveSelection ? moveSelection : undefined,
        moveLabel,
        onConfirmCounterparties: canConfirmCounterparties
            ? confirmCounterpartiesSelection
            : undefined,
        counterpartyLabel,
    });
    const discardQuestion = discardQuestionForSelection({
        selectedRows: rowsToDiscard,
        documentRows,
        mode,
        itemLabel,
    });
    const discardSelected = useCallback(async () => {
        if (!rowsToDiscard) {
            return;
        }
        const omittedExtractionCount = omittedExtractionCountForDiscard({
            selectedRows: rowsToDiscard,
            documentRows,
            mode,
        });
        const results = await removeDocuments(
            documentRowsForDraftSelection({
                selectedRows: rowsToDiscard,
                documentRows,
            }),
        );
        const failedCount = results.filter(
            (result) => result.status === "rejected",
        ).length;
        const discardedCount = results.length - failedCount;
        setRowsToDiscard(null);
        setRowSelection({});
        if (failedCount === 0) {
            success(
                discardedDraftsMessage({
                    discardedCount,
                    omittedExtractionCount,
                    itemLabel,
                }),
            );
            return;
        }
        error(
            `Discarded ${discardedCount}. ${failedCount} ${pluralize("draft", failedCount)} failed.`,
        );
    }, [
        documentRows,
        error,
        itemLabel,
        mode,
        removeDocuments,
        rowsToDiscard,
        success,
    ]);

    return (
        <>
            <AccrualImporterStats rows={visibleRows} />
            <div className={draftsViewStyles.tableBody}>
                <ViewTable
                    className={invoicesViewStyles.invoicesView}
                    data={pageData}
                    onRowClick={(row) => {
                        if (row?.financialDocument) {
                            setPreviewedRowId(
                                row.documentRowId ?? row.id,
                                row.extractionId,
                            );
                        }
                    }}
                    fixedTopOffsetPx
                    enableKeyboardShortcuts
                    view={tableView}
                    config={viewConfig}
                    tableMeta={tableMeta}
                    sort={sort}
                    onSortChange={setSort}
                    onSortRemove={() => setSort(undefined)}
                    onFocusedRowChange={(_index, row) => {
                        setFocusedRow(row);
                        if (!row?.financialDocument) {
                            setIsQuickLookOpen(false);
                        }
                    }}
                    onPreviewFocused={(row) => {
                        if (!row.financialDocument) {
                            return;
                        }
                        setFocusedRow(row);
                        setIsQuickLookOpen((isOpen) => !isOpen);
                    }}
                    rowSelection={rowSelection}
                    onRowSelectionChange={setRowSelection}
                    enableRowSelection={(row) => !!row.financialDocument}
                    footer={
                        selectedRows.length > 0 ? (
                            <AccrualImporterDraftsCommandBar
                                selectedCount={selectedRows.length}
                                selectableCount={selectableRows.length}
                                onClearSelection={() => setRowSelection({})}
                                onSelectAll={selectAll}
                                onOpenActions={handleOpenBulkActions}
                                reviewAction={reviewAction}
                            />
                        ) : undefined
                    }
                />
            </div>
            {rerunConfirm}
            <ConfirmationModal
                show={rowsToDiscard !== null}
                title="Discard drafts?"
                question={discardQuestion}
                yes="Discard"
                yesBtnVariant="destructive"
                no="Cancel"
                onHide={() => setRowsToDiscard(null)}
                onConfirm={discardSelected}
            />
            <AccrualImporterQuickLook
                financialDocument={
                    isQuickLookOpen
                        ? (focusedRow?.financialDocument ?? null)
                        : null
                }
                onClose={() => setIsQuickLookOpen(false)}
            />
        </>
    );
};
